/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

import { IBcAdditionalShipToInfoData } from './bc-additional-ship-to-info.data';
import { IBcAdditionalShipToInfoProps } from './bc-additional-ship-to-info.props.autogenerated';

import { IModuleStateProps, withModuleState } from '@msdyn365-commerce-modules/checkout-utilities';
import { observer } from 'mobx-react';
import { getCheckoutState } from '@msdyn365-commerce/global-state';
import { updateAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';
import { AttributeValue } from '@msdyn365-commerce/retail-proxy';

export interface IBcAdditionalShipToViewProps extends IBcAdditionalShipToInfoProps<IBcAdditionalShipToInfoData> {
    input: React.ReactNode;
    additionalShipToInfo: React.ReactNode;
}

interface IBcCheckoutAdditionalShipToInfoProps extends IBcAdditionalShipToInfoProps<IBcAdditionalShipToInfoData>, IModuleStateProps {}

interface IBcCheckoutAdditionalShipToInfoState {
    additionalShipToInfo: string;
    originalAdditionalShipToInfo: string;
}

/**
 *
 * BcAdditionalShipToInfo component
 * @extends {React.PureComponent<IBcAdditionalShipToInfoProps<IBcAdditionalShipToInfoData>>}
 */
// @ts-expect-error
@withModuleState
@observer
class BcAdditionalShipToInfo extends React.PureComponent<IBcCheckoutAdditionalShipToInfoProps, IBcCheckoutAdditionalShipToInfoState> {
    public state: IBcCheckoutAdditionalShipToInfoState = {
        additionalShipToInfo: '',
        originalAdditionalShipToInfo: ''
    };

    public componentDidMount(): void {
        this.init();
    }

    public handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        this.setState({ additionalShipToInfo: value });
    };
    public render(): JSX.Element | null {
        const { moduleState } = this.props;
        const viewProps = {
            ...this.props,
            input: moduleState.isUpdating && (
                <input
                    className='ms-checkout-guest-profile__input-text form-control'
                    type='text'
                    placeholder={this.props.config.inputPlaceholder}
                    value={this.state.additionalShipToInfo}
                    onChange={this.handleInputChange}
                    maxLength={this.props.config.maxCharacterLength}
                />
            ),
            additionalShipToInfo: (moduleState.isReady || moduleState.isPending) && (
                <span className='ms-checkout-guest-profile__selected-email'>{this.state.additionalShipToInfo}</span>
            )
        };

        return this.props.renderView(viewProps);
    }

    private readonly init = (): void => {
        const { additionalShipToInformation } = this.props.context.app.config;
        const checkoutState = this.props.data.checkout.result;
        const cart = checkoutState?.checkoutCart.cart;
        const checkoutAttributes = cart?.AttributeValues as AttributeValue[];
        const additionalShipToInfoAttribute = checkoutAttributes?.find(attr => attr.Name === additionalShipToInformation);
        if (
            additionalShipToInfoAttribute &&
            additionalShipToInfoAttribute.TextValue &&
            additionalShipToInfoAttribute.TextValue !== '' &&
            additionalShipToInfoAttribute.TextValue !== 'None'
        ) {
            this.setState({
                additionalShipToInfo: additionalShipToInfoAttribute.TextValue,
                originalAdditionalShipToInfo: additionalShipToInfoAttribute.TextValue
            });
        }
        this.props.moduleState.init({
            onEdit: this.onEdit,
            onCancel: this.onCancel,
            onSubmit: this.onSubmit,
            status:
                additionalShipToInfoAttribute &&
                additionalShipToInfoAttribute.TextValue !== '' &&
                additionalShipToInfoAttribute.TextValue !== 'None'
                    ? 'ready'
                    : 'updating'
        });
        if (checkoutState) {
            if (checkoutState.checkoutCart.hasInvoiceLine) {
                this.props.moduleState.onSkip();
                this.props.moduleState.onDisable();
            }
        }
    };

    private readonly onCancel = (): void => {
        this.props.telemetry.information('PO Number onCancel is called.');

        this.setState(
            prevState => ({
                additionalShipToInfo: prevState.originalAdditionalShipToInfo
            }),
            () => {
                this.props.moduleState.onReady();
            }
        );
    };

    private readonly onEdit = (): void => {
        this.props.telemetry.information('PO Number onEdit is called.');

        this.props.moduleState.onUpdating();
    };

    private readonly onSubmit = async (): Promise<void> => {
        const { additionalShipToInfo } = this.state;
        this.onPending();
        const { additionalShipToInformation } = this.props.context.app.config;
        const checkoutState = await getCheckoutState(this.props.context.actionContext);
        const cart = checkoutState.checkoutCart.cart;
        const checkoutAttributes = cart.AttributeValues as AttributeValue[];
        const additionalShipToInfoAttributeIndex = checkoutAttributes?.findIndex(attr => attr.Name === additionalShipToInformation);
        if (additionalShipToInfoAttributeIndex >= 0) {
            checkoutAttributes[additionalShipToInfoAttributeIndex].TextValue = additionalShipToInfo;
        } else {
            const attributeObj = {
                // @ts-ignore
                '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
                Name: additionalShipToInformation,
                TextValue: additionalShipToInfo,
                TextValueTranslations: []
            };
            checkoutAttributes.push(attributeObj);
        }
        await checkoutState.checkoutCart.updateAttributeValues({
            newAttributeValues: checkoutAttributes
        });
        const updatedCheckoutState = await getCheckoutState(this.props.context.actionContext);

        await updateAsync({ callerContext: this.props.context.actionContext }, updatedCheckoutState.checkoutCart.cart);
        await checkoutState.checkoutCart.refreshCart({});
        this.setState({ originalAdditionalShipToInfo: additionalShipToInfo });
        this.props.telemetry.information('PO Number onSubmit is called.');

        this.props.moduleState.onReady();
    };

    private readonly onPending = () => {
        this.props.moduleState.onPending();
        if (this.props.data && this.props.data.checkout && this.props.data.checkout.result) {
            if (this.props.data.checkout.result.checkoutCart.hasInvoiceLine) {
                this.props.moduleState.onSkip();
                this.props.moduleState.onDisable();
            }
        }
    };
}

export default BcAdditionalShipToInfo;
